import { useEffect, useSyncExternalStore } from "react";

export type Theme = "light" | "dark";
const STORAGE_KEY = "chonburi:theme";

function readSavedTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

// The shell, basemap and top-bar control share one preference. Independent
// hook state previously changed the CSS theme while leaving the map dark.
let currentTheme = readSavedTheme();
const listeners = new Set<() => void>();
const snapshot = () => currentTheme;
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};
const setTheme = (theme: Theme) => {
  currentTheme = theme;
  try { window.localStorage.setItem(STORAGE_KEY, theme); } catch { /* Private browsing keeps the in-memory preference. */ }
  listeners.forEach(listener => listener());
};

export function useTheme(): { theme: Theme; setTheme: (theme: Theme) => void; toggle: () => void } {
  const theme = useSyncExternalStore(subscribe, snapshot, () => "dark" as Theme);
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.style.colorScheme = theme;
  }, [theme]);
  return { theme, setTheme, toggle: () => setTheme(theme === "light" ? "dark" : "light") };
}
